import { clone } from "lodash";

const NULL_BYTE = "\u0000";

/**
 * Removes null bytes from every string in the value tree. PostgreSQL rejects them in text and
 * jsonb columns, so they must never reach the database.
 *
 * Walks the tree once and returns the original value untouched when it contains no null bytes,
 * so the common case allocates nothing.
 */
export function stripNullBytes<T>(value: T): T {
  return stripNullBytesInternal(value) as T;
}

function stripNullBytesInternal(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "string") {
    return value.includes(NULL_BYTE) ? value.replaceAll(NULL_BYTE, "") : value;
  }

  if (Array.isArray(value)) {
    // Lazy allocation: the shallow copy is only made once a change is actually found, at which
    // point it starts out holding every element (cleaned ones so far, original ones after) and the
    // loop below overwrites each remaining index with its cleaned value as it reaches it.
    let result: unknown[] | null = null;
    for (let index = 0; index < value.length; index++) {
      const item = value[index];
      const cleaned = stripNullBytesInternal(item);
      if (cleaned !== item && result === null) {
        result = clone(value);
      }
      if (result !== null) {
        result[index] = cleaned;
      }
    }
    return result ?? value;
  }

  if (typeof value !== "object") {
    return value;
  }

  // Values that serialize themselves (DateTime, UTCDate, BerlinDate, Date) are opaque leaves:
  // rebuilding them from their own properties would destroy what toJSON() emits.
  if (typeof (value as { toJSON?: unknown }).toJSON === "function") {
    return value;
  }

  // Same lazy-allocation shape as the array branch above, and for-in instead of Object.entries so
  // walking a clean object allocates no intermediate array either.
  let result: Record<string, unknown> | null = null;
  for (const key in value as Record<string, unknown>) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      continue;
    }
    const item = (value as Record<string, unknown>)[key];
    const cleaned = stripNullBytesInternal(item);
    if (cleaned !== item && result === null) {
      result = clone(value as Record<string, unknown>);
    }
    if (result !== null) {
      result[key] = cleaned;
    }
  }

  if (result === null) {
    return value;
  }

  // Entities reach this function as class instances (e.g. AuditEntity), not plain objects. Keeping
  // the prototype preserves their class identity for class-validator and downstream instanceof
  // checks; for a plain object this yields a plain object again.
  return Object.assign(Object.create(Object.getPrototypeOf(value) as object | null), result);
}
